import { Component, inject } from '@angular/core';
import { EChartsOption } from 'echarts';

import { CLOSE_LOOP_KPIS } from '../../data/close-loop.data';
import { NPS_GENERAL as NPS_GENERAL_DETALLE, NPS_HISTORICO } from '../../data/dashboard.data';
import { FILTROS_BASE, FILTROS_DEFAULT } from '../../data/filters.data';
import {
  HISTORICO_COMERCIANTE as MENSUAL_COMERCIANTE,
  HISTORICO_CONSUMIDOR as MENSUAL_CONSUMIDOR,
  ISucursalNps,
  NPS_COMERCIANTE,
  NPS_CONSUMIDOR,
  NPS_GENERAL,
  NPS_MENSUAL,
  NPS_SUCURSALES,
  PERIODO,
} from '../../data/home.data';
import {
  HISTORICO_COMERCIANTE as HISTORICO_COMERCIANTE_DETALLE,
  HISTORICO_CONSUMIDOR as HISTORICO_CONSUMIDOR_DETALLE,
} from '../../data/results.data';
import { FilterField } from '../../models/filter-option.model';
import { INpsSummary } from '../../models/nps-summary.model';
import { ISeriesPoint } from '../../models/series-point.model';
import { ChartService } from '../../services/chart.service';
import { ExportService, IExportBlock } from '../../services/export.service';
import { FilterService, IFilterContext } from '../../services/filter.service';
import { NpsService } from '../../services/nps.service';

/** Vista de las series históricas: acumulado del año o ventana móvil. */
export type SeriesMode = 'ytd' | 'rolling';

/** Fila ya resuelta de las listas Top 5 / Bottom 5. */
export interface IRankRow {
  /** Posición dentro del ranking completo de sucursales. */
  posicion: number;
  sucursal: string;
  nps: number;
  /** Color del semáforo, siempre como token. */
  color: string;
  /** Lectura del semáforo para quien no percibe el color. */
  nivel: string;
}

/**
 * Semáforo del ranking. `NpsService.colorClass()` resuelve el umbral (>= 65 /
 * < 65 / < 50); aquí sólo se traduce su clase al token de color y a la palabra
 * que lo acompaña, porque las clases globales `.nps-*` pintan celdas de tabla,
 * no barras de progreso.
 */
const SEMAFORO: Record<string, { color: string; nivel: string }> = {
  'nps-good': { color: 'var(--z-promotor)',  nivel: 'nivel alto' },
  'nps-mid':  { color: 'var(--z-pasivo)',    nivel: 'nivel medio' },
  'nps-bad':  { color: 'var(--z-detractor)', nivel: 'nivel bajo' },
};

/** Cuántas sucursales entran en cada lista destacada. */
const DESTACADAS = 5;

/**
 * Sección «Pulso» — ¿cómo vamos?
 *
 * Es la portada y la única sección sin pestañas: una sola narrativa de arriba
 * a abajo, del número general al detalle por tipo de cliente y por sucursal.
 *
 * Todo lo que se pinta se recalcula en `aplicar()`, que corre en el constructor
 * y cada vez que cambian los filtros. Los campos no son `readonly` por eso: la
 * página es una vista del periodo y del universo elegidos, no de la constante.
 */
@Component({
  selector: 'app-pulse-page',
  standalone: false,
  templateUrl: './pulse-page.component.html',
  styleUrl: './pulse-page.component.scss',
})
export class PulsePageComponent {

  private readonly chartService = inject(ChartService);
  private readonly npsService = inject(NpsService);
  private readonly filterService = inject(FilterService);
  private readonly exportService = inject(ExportService);

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR filtros                              */
  /* -------------------------------------------------------------------------- */

  public readonly filtros: Array<FilterField> = FILTROS_BASE;
  public seleccion: Record<string, Array<string>> = { ...FILTROS_DEFAULT };

  private ctx: IFilterContext = this.filterService.contexto(this.seleccion);

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR datos                                */
  /* -------------------------------------------------------------------------- */

  public periodo: string = PERIODO;

  /** Qué está acotando la vista, si es que algo la acota. */
  public alcance = '';

  public general: INpsSummary = NPS_GENERAL;
  public generalDetalle: INpsSummary = NPS_GENERAL_DETALLE;
  public comerciante: INpsSummary = NPS_COMERCIANTE;
  public consumidor: INpsSummary = NPS_CONSUMIDOR;

  public totalSucursales: number = NPS_SUCURSALES.length;
  public topSucursales: Array<IRankRow> = [];
  public bottomSucursales: Array<IRankRow> = [];

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR banda de KPIs                           */
  /* -------------------------------------------------------------------------- */

  /** Los tres NPS se comparan contra el mes anterior de su ventana móvil. */
  public deltaGeneral: number | undefined;
  public deltaComerciante: number | undefined;
  public deltaConsumidor: number | undefined;
  public hintPeriodo = '';

  /** Alertas que siguen abiertas: las que están en proceso más las no atendidas. */
  public alertasEnProceso = 0;
  public alertasNoAtendidas = 0;
  public alertasActivas = 0;
  public alertasHint = '';

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR gráficas                              */
  /* -------------------------------------------------------------------------- */

  public modoTendencia: SeriesMode = 'rolling';

  public graficaTendencia!: EChartsOption;
  public donaGeneral!: EChartsOption;
  public graficaHistorico!: EChartsOption;
  public graficaComerciante!: EChartsOption;
  public graficaConsumidor!: EChartsOption;

  /**
   * Los puntos de cada serie, aparte de la `EChartsOption`.
   *
   * Una opción de ECharts no se puede exportar: los valores viven enterrados
   * en `series[0].data` junto con degradados y formateadores. Guardar la serie
   * cruda es lo que permite que «Descargar» baje exactamente lo que se pintó.
   */
  private serieTendencia: Array<ISeriesPoint> = [];
  private serieHistorico: Array<ISeriesPoint> = [];
  private serieComerciante: Array<ISeriesPoint> = [];
  private serieConsumidor: Array<ISeriesPoint> = [];

  constructor() {
    this.aplicar();
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR acciones                              */
  /* -------------------------------------------------------------------------- */

  public onFiltros(valores: Record<string, Array<string>>): void {
    this.seleccion = valores;
    this.ctx = this.filterService.contexto(valores);
    this.aplicar();
  }

  public setModoTendencia(modo: SeriesMode): void {
    if (this.modoTendencia === modo) {
      return;
    }

    this.modoTendencia = modo;
    this.serieTendencia = this.filterService.serie(NPS_MENSUAL[modo], this.ctx, 'mensual');
    this.graficaTendencia = this.chartService.npsLine(
      this.serieTendencia,
      ChartService.COLORS.yellow,
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR recálculo                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Rehace la sección entera contra el contexto vigente.
   *
   * Con la selección vacía el servicio de filtros es la identidad, así que esto
   * reproduce exactamente las cifras del tablero original.
   */
  private aplicar(): void {
    const ctx = this.ctx;

    this.periodo = this.filterService.etiquetaPeriodo(ctx, PERIODO);
    this.alcance = this.filterService.resumenAlcance(ctx);

    /* --- resúmenes ---------------------------------------------------------- */
    this.comerciante = this.filterService.resumen(NPS_COMERCIANTE, ctx, 'comerciante');
    this.consumidor = this.filterService.resumen(NPS_CONSUMIDOR, ctx, 'consumidor');
    this.general = this.resumenGeneral(NPS_GENERAL, 'general');
    this.generalDetalle = this.resumenGeneral(NPS_GENERAL_DETALLE, 'general-detalle');
    this.generalDetalle = { ...this.generalDetalle, period: this.periodo };

    /* --- sucursales --------------------------------------------------------- */
    const sucursales = this.filterService.porSucursal(NPS_SUCURSALES, ctx, 'sucursal')
      .map((fila) => ({
        ...fila,
        nps: this.filterService.desviar(fila.nps, ctx, `sucursal:${fila.sucursal}`, 5, 10, 99),
      }));

    this.totalSucursales = sucursales.length;
    this.topSucursales = this.destacadas(sucursales, 'top');
    this.bottomSucursales = this.destacadas(sucursales, 'bottom');

    /* --- KPIs de cierre ----------------------------------------------------- */
    this.alertasEnProceso = this.kpiCierre('En proceso');
    this.alertasNoAtendidas = this.kpiCierre('No atendidas');
    this.alertasActivas = this.alertasEnProceso + this.alertasNoAtendidas;
    this.alertasHint =
      `${this.alertasEnProceso} en proceso · ${this.alertasNoAtendidas} sin atender`;

    /* --- series ------------------------------------------------------------- */
    this.serieTendencia = this.filterService.serie(NPS_MENSUAL[this.modoTendencia], ctx, 'mensual');
    this.serieHistorico = this.filterService.serie(NPS_HISTORICO, ctx, 'historico');
    this.serieComerciante = this.filterService.serie(
      HISTORICO_COMERCIANTE_DETALLE, ctx, 'hist-comerciante');
    this.serieConsumidor = this.filterService.serie(
      HISTORICO_CONSUMIDOR_DETALLE, ctx, 'hist-consumidor');

    this.deltaGeneral = this.variacion(this.serieTendencia);
    this.deltaComerciante = this.variacion(
      this.filterService.serie(MENSUAL_COMERCIANTE.rolling, ctx, 'mensual-comerciante'));
    this.deltaConsumidor = this.variacion(
      this.filterService.serie(MENSUAL_CONSUMIDOR.rolling, ctx, 'mensual-consumidor'));
    this.hintPeriodo = this.etiquetaAnterior(this.serieTendencia);

    /* --- gráficas ----------------------------------------------------------- */
    this.donaGeneral = this.chartService.donut(
      this.generalDetalle.nps,
      ChartService.COLORS.green,
    );
    this.graficaTendencia = this.chartService.npsLine(
      this.serieTendencia,
      ChartService.COLORS.yellow,
    );
    this.graficaHistorico = this.chartService.npsArea(
      this.serieHistorico,
      ChartService.COLORS.teal,
    );
    this.graficaComerciante = this.chartService.npsLine(
      this.serieComerciante,
      ChartService.COLORS.magenta,
      90,
    );
    this.graficaConsumidor = this.chartService.npsLine(
      this.serieConsumidor,
      ChartService.COLORS.teal,
      90,
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR exportación                             */
  /* -------------------------------------------------------------------------- */

  /**
   * «Descargar» baja **la sección entera** en un solo CSV: la banda de KPIs, el
   * desglose, las tres series y los dos extremos del ranking, cada bloque con
   * su título. Pulso no es una tabla —son seis bloques—, y repartirlos en seis
   * descargas sería peor que un archivo con seis apartados.
   */
  public descargar(): void {
    this.exportService.csv(
      this.bloques(),
      this.exportService.nombreArchivo('nps', 'pulso'),
      {
        titulo: 'Pulso · ¿Cómo vamos?',
        periodo: this.periodo,
        filtros: this.filterService.descripcion(this.seleccion, this.filtros),
      },
    );
  }

  private bloques(): Array<IExportBlock> {
    return [
      {
        titulo: 'Indicadores del periodo',
        columnas: [
          { key: 'indicador', title: 'Indicador' },
          { key: 'valor', title: 'Valor' },
          { key: 'variacion', title: 'Variación vs. periodo anterior' },
        ],
        filas: [
          { indicador: 'NPS general', valor: this.general.nps, variacion: this.deltaGeneral ?? '' },
          { indicador: 'Comerciante', valor: this.comerciante.nps, variacion: this.deltaComerciante ?? '' },
          { indicador: 'Consumidor final', valor: this.consumidor.nps, variacion: this.deltaConsumidor ?? '' },
          { indicador: 'Alertas activas', valor: this.alertasActivas, variacion: '' },
          { indicador: 'Alertas en proceso', valor: this.alertasEnProceso, variacion: '' },
          { indicador: 'Alertas sin atender', valor: this.alertasNoAtendidas, variacion: '' },
        ],
      },
      {
        titulo: 'NPS general · desglose',
        columnas: [
          { key: 'grupo', title: 'Grupo' },
          { key: 'porcentaje', title: 'Porcentaje', suffix: '%' },
        ],
        filas: [
          { grupo: 'Detractor', porcentaje: this.generalDetalle.detractor },
          { grupo: 'Pasivo', porcentaje: this.generalDetalle.pasivo },
          { grupo: 'Promotor', porcentaje: this.generalDetalle.promotor },
          { grupo: 'Neto de recomendación', porcentaje: this.generalDetalle.nps },
        ],
      },
      this.bloqueSerie(
        `Tendencia mensual (${this.modoTendencia === 'ytd' ? 'YTD' : 'Rolling'})`,
        this.serieTendencia,
      ),
      this.bloqueSerie('Histórico de trece meses', this.serieHistorico),
      this.bloqueSerie('Histórico · comerciante', this.serieComerciante),
      this.bloqueSerie('Histórico · consumidor final', this.serieConsumidor),
      this.bloqueRanking('Top 5 sucursales', this.topSucursales),
      this.bloqueRanking('Bottom 5 sucursales', this.bottomSucursales),
    ];
  }

  private bloqueSerie(titulo: string, puntos: Array<ISeriesPoint>): IExportBlock {
    return {
      titulo,
      columnas: [
        { key: 'label', title: 'Mes' },
        { key: 'value', title: 'NPS', suffix: '%' },
        { key: 'count', title: 'Encuestas' },
      ],
      filas: puntos.map((punto) => ({ ...punto, count: punto.count ?? '' })),
    };
  }

  private bloqueRanking(titulo: string, filas: Array<IRankRow>): IExportBlock {
    return {
      titulo,
      columnas: [
        { key: 'posicion', title: 'Posición' },
        { key: 'sucursal', title: 'Sucursal' },
        { key: 'nps', title: 'NPS', suffix: '%' },
        { key: 'nivel', title: 'Semáforo' },
      ],
      filas,
    };
  }

  /**
   * El NPS general con un tipo de cliente elegido deja de ser un promedio: es
   * el de ese segmento, que sí está en los datos. Sólo cuando no hay segmento
   * elegido se recurre a la derivación.
   */
  private resumenGeneral(base: INpsSummary, clave: string): INpsSummary {
    if (this.ctx.tipoCliente === 'Comerciante') {
      return { ...this.comerciante, period: base.period };
    }

    if (this.ctx.tipoCliente === 'Consumidor final') {
      return { ...this.consumidor, period: base.period };
    }

    return this.filterService.resumen(base, this.ctx, clave);
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR derivados                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Variación contra el punto anterior de la misma serie. Es un cálculo sobre
   * los datos que ya existen: no introduce cifras nuevas.
   */
  private variacion(puntos: Array<ISeriesPoint>): number | undefined {
    if (puntos.length < 2) {
      return undefined;
    }

    return puntos[puntos.length - 1].value - puntos[puntos.length - 2].value;
  }

  /** «vs. Jun-26»: contra qué punto se está comparando la variación. */
  private etiquetaAnterior(puntos: Array<ISeriesPoint>): string {
    const anterior = puntos[puntos.length - 2];

    return anterior ? `vs. ${anterior.label}` : '';
  }

  private kpiCierre(label: string): number {
    const base = CLOSE_LOOP_KPIS.find((kpi) => kpi.label === label)?.value ?? 0;

    return Math.max(0, Math.round(base * this.filterService.factorMuestra(this.ctx)));
  }

  /**
   * Cinco primeras o cinco últimas del ranking. La copia es obligatoria:
   * `sort()` ordena en el sitio y `NPS_SUCURSALES` es la constante compartida.
   */
  private destacadas(filas: Array<ISucursalNps>, extremo: 'top' | 'bottom'): Array<IRankRow> {
    const orden: Array<ISucursalNps> = [...filas].sort((a, b) => b.nps - a.nps);
    const total = orden.length;

    const seleccion = extremo === 'top'
      ? orden.slice(0, DESTACADAS).map((fila, i) => ({ fila, posicion: i + 1 }))
      : orden.slice(-DESTACADAS).reverse().map((fila, i) => ({ fila, posicion: total - i }));

    return seleccion.map(({ fila, posicion }) => {
      const semaforo = SEMAFORO[this.npsService.colorClass(fila.nps)];

      return {
        posicion,
        sucursal: fila.sucursal,
        nps: fila.nps,
        color: semaforo?.color ?? 'var(--z-text-muted)',
        nivel: semaforo?.nivel ?? '',
      };
    });
  }
}
